/**
 * Announcement model translations
 *
 * Translation keys follow the pattern: models.announcement.{category}.{key}
 * Categories: fields, tabs, blocks, badges, actions, filters
 *
 * Note: vue-i18n resolves dots as nested paths, so nested field keys
 * like "extra_attributes.recipient_targeting" must be structured as nested objects.
 */
export default {
  en: {
    // Model names
    singular: "Announcement",
    plural: "Announcements",

    // Blocks
    blocks: {
      announcement: "Announcement",
      Announcement: "Announcement", // TitleCase fallback: class_basename() returns "Announcement"

      header: "Announcement",
      body: "Content",
      classification: "Priority & Status",
      schedule: "Scheduling",
      engagement: "Engagement",
      training: "Required trainings",
      recipient_targeting: "Recipient Targeting",
      attachments: "Attachments",
      announcement_details: "Announcement Details",
      schedule_manager: "Schedule Manager",
      schedule_history: "Schedule History",
      send_now_recipients: "Recipients",
      schedule_recipients: "Recipients",
    },

    // Fields - nested structure for dot-notation keys
    fields: {
      title: "Title",
      content: "Content",
      priority: "Priority",
      status: "Status",
      sender: "Sender",
      sent_at: "Sent At",
      expires_at: "Expires At",
      channels: "Delivery Channels",
      attachments: "Attachments",
      requires_acknowledgment: "Requires Acknowledgment",
      announcement_type_id: "Memo type",
      formations: "Required trainings",
      extra_attributes: {
        recipient_targeting: "Recipients",
        is_onboarding: "Auto-send to New Employees",
        requires_acknowledgment: "Requires Acknowledgment",
        requires_signature: "Requires Signature",
      },
    },

    // Tabs
    tabs: {
      content: "Content",
      details: "Details",
      attachments: "Attachments",
      recipients: "Recipients",
      analytics: "Analytics",
    },

    // Options (for select/enum form fields and faceted filters)
    options: {
      status: {
        draft: "Draft",
        sent: "Sent",
        archived: "Archived",
      },
      priority: {
        low: "Low",
        normal: "Normal",
        high: "High",
        urgent: "Urgent",
      },
      channels: {
        in_app: "In-App",
        push: "Push",
        email: "Email",
      },
    },

    // Badges (for select options and badge columns)
    badges: {
      draft: "Draft",
      scheduled: "Scheduled",
      sent: "Sent",
      archived: "Archived",
      low: "Low",
      normal: "Normal",
      high: "High",
      urgent: "Urgent",
      in_app: "In-App",
      push: "Push",
      email: "Email",
    },

    // Actions
    actions: {
      send: "Send",
      schedule: "Schedule",
      archive: "Archive",
      bulk_send: "Send Selected",
      bulk_archive: "Archive Selected",
    },

    // Filters
    filters: {
      status: "Status",
      priority: "Priority",
      is_onboarding: "Onboarding",
      is_scheduled: "Scheduled",
    },

    // Schedule Manager
    schedule_manager: {
      title: "Schedule Manager",
      send_now: "Send Now",
      schedule_new: "Schedule New",
      send_now_description: "Send this communication immediately to selected recipients.",
      schedule: "Schedule",
      date: "Date",
      time: "Time",
      frequency: "Frequency",
      scheduled_time_future: "Scheduled time must be at least 5 minutes in the future.",
      scheduled_time_min_hint: "Pick a time at least 5 minutes from now.",
      select_recipient: "Please select at least one recipient.",
      archived_unavailable: "Scheduling is not available for archived communications.",
      schedule_history: "Schedule History",
      no_scheduled_items: "No scheduled items found.",
      scheduled_for: "Scheduled for: :date",
      executed: "Executed",
      executed_at: "Executed: :date",
      cancelled: "Cancelled",
      cancelled_at: "Cancelled: :date",
      pending: "Pending",
      cancel: "Cancel",
      retract: "Retract",
      retract_blocked: "Cannot retract: at least one recipient has already read this message.",
      retract_confirm: "Retract this announcement from all recipient inboxes? This cannot be undone.",
    },

    // Recipient Picker
    recipients: {
      title: "Recipients",
      target_audience: "Target Audience",
      specific_users: "Specific Users",
      specific_users_description: "Select individual users",
      by_roles: "By Role",
      by_roles_description: "Target users by role",
      all_users: "All Users",
      all_users_description: "Send to everyone",
      all_users_message: "All users will receive this communication",
      all_users_description_full: "This communication will be sent to everyone in the system.",
      select_users: "Select Users",
      select_roles: "Select Roles",
      select_all: "Select all",
      deselect_all: "Deselect all",
      exclude_users: "Exclude specific users (optional)",
      search_users: "Search users...",
      search_users_exclude: "Search users to exclude...",
      type_to_search: "Type to search users...",
      searching: "Searching...",
      type_min_chars: "Type at least 2 characters to search",
      no_users_found: "No users found",
      users: "Users",
      selected: "Selected",
      users_selected: ":count / :max users selected",
      maximum_selected: "Maximum :max users selected",
      more: "+:count more",
      preview: "Preview",
      configure_preview: "Configure recipients above to see a preview",
      calculating: "Calculating recipients...",
      preview_failed: "Failed to preview recipients",
      no_users_match: "No users match this criteria",
      try_different: "Try selecting different users or roles",
      recipient: "recipient",
      recipients_plural: "recipients",
      large_audience: "Large audience",
      show_sample: "Show sample recipients",
      hide_sample: "Hide sample recipients",
      cannot_modify: "Recipients cannot be modified after the communication has been sent.",
      role: "role",
      roles: "roles",
      user: "user",
      users_plural: "users",
      total: "total",
      roles_selected: ":count :role selected",
      no_roles_available: "No roles available",
      failed_to_load_roles: "Failed to load roles",
      try_again: "Try again",
    },

    // Responder Picker
    responders: {
      title: "Conversation Responders",
      description: "Select who will be automatically added to conversations when recipients reply.",
      search_users: "Search users to add as responders...",
      preview: "Responders Preview",
      configure_preview: "Select roles or users above to see a preview",
      calculating: "Resolving responders...",
      preview_failed: "Failed to preview responders",
      no_responders_match: "No responders match the selected criteria",
      responder: "responder",
      responders_plural: "responders",
      cannot_modify: "Responders cannot be modified after the communication has been sent.",
      permissions: "Responder Permissions",
      can_view: "Can view conversation messages",
      can_reply: "Can reply to conversations",
      none_summary: "No responders",
    },

    // Analytics Panel
    analytics: {
      title: "Analytics",
      not_available: "Analytics not available",
      not_available_description: "Analytics will be available after sending this communication.",
      scheduled_delivery: "Scheduled for delivery",
      scheduled_delivery_description: "Analytics will be available after the scheduled delivery time.",
      failed_to_load: "Failed to load analytics",
      retry: "Retry",
      sent: "Sent",
      read: "Read",
      read_rate: "Read Rate",
      acknowledged: "Acknowledged",
      delivery_funnel: "Delivery Funnel",
      funnel_chip_total: ":count total",
      funnel_chip_unique: ":count unique",
      prior_delivery_label: "Earlier send #:n",
      delivery_summary: "Delivery Summary",
      total_recipients: "Total Recipients",
      delivery_rate: "Delivery Rate",
      open_rate: "Open Rate",
      acknowledgment_rate: "Acknowledgment Rate",
      avg_response_time: "Avg. Response Time",
      no_analytics_yet: "No analytics yet",
      send_to_see: "Send this communication to see delivery analytics.",
      recipients: "Recipients",
      search_recipients: "Search recipients...",
      filter_by_status: "Filter by status",
      all: "All",
      pending: "Pending",
      sent_label: "Sent",
      read_label: "Read",
      pending_ack: "Pending Ack.",
      acknowledged_label: "Acknowledged",
      recipient_column: "Recipient",
      status_column: "Status",
      sent_column: "Sent",
      read_column: "Read",
      acknowledged_column: "Acknowledged",
      no_recipients_match: "No recipients match your filters",
      recipient_count: ":count :label",
      page: "Page :current of :total",
      csv: "CSV",
      excel: "Excel",
      just_now: "Just now",
      minutes_ago: ":countm ago",
      hours_ago: ":counth ago",
      days_ago: ":countd ago",
      pending_signature: "Pending Signature",
      signed: "Signed",
      view_signature: "View Signature",
      signature_column: "Signature",
      signature_viewer_title: "Signature",
      signed_by: "Signed by :name",
      signed_on: "Signed on :date",
      signature_rate: "Signature Rate",
    },

    // Communication Detail Page
    detail: {
      from: "From:",
      sent: "Sent:",
      read: "Read:",
      acknowledged_on: "Acknowledged on :date",
      requires_acknowledgment: "Requires acknowledgment",
      attachments: "Attachments (:count)",
      download: "Download",
      acknowledgment_required: "Read confirmation",
      acknowledgment_description: "Please confirm you have read this message.",
      acknowledgment_checkbox: "I have read and understood this communication",
      comment_optional: "Comment (optional)",
      add_comment: "Add a comment...",
      acknowledge: "Acknowledge",
      acknowledging: "Acknowledging...",
    },

    // Schedule Frequency
    schedule: {
      frequency: {
        once: "Once",
        daily: "Daily",
        weekly: "Weekly",
        monthly: "Monthly",
      },
    },

    // Placeholders
    placeholders: {
      select_date_time: "Select date and time",
      media_placeholder: "Select media files",
    },

    // Media Library Integration
    media: {
      requires_spatie: "(Requires Spatie Media Library integration)",
    },
  },

  fr: {
    // Model names
    singular: "Mémo",
    plural: "Mémos",

    // Blocks
    blocks: {
      announcement: "Annonce",
      Announcement: "Annonce", // TitleCase fallback: class_basename() returns "Announcement"

      header: "Annonce",
      body: "Contenu",
      classification: "Priorité et statut",
      schedule: "Programmation",
      engagement: "Engagement",
      training: "Formations requises",
      recipient_targeting: "Ciblage des destinataires",
      attachments: "Pièces jointes",
      announcement_details: "Détails de l'annonce",
      schedule_manager: "Gestionnaire de planification",
      schedule_history: "Historique de planification",
      send_now_recipients: "Destinataires",
      schedule_recipients: "Destinataires",
    },

    // Fields - nested structure for dot-notation keys
    fields: {
      title: "Titre",
      content: "Contenu",
      priority: "Priorité",
      status: "Statut",
      sender: "Expéditeur",
      sent_at: "Envoyé le",
      expires_at: "Expire le",
      channels: "Canaux de livraison",
      attachments: "Pièces jointes",
      requires_acknowledgment: "Nécessite acceptation (case à cocher)",
      announcement_type_id: "Type de mémo",
      formations: "Formations requises",
      extra_attributes: {
        recipient_targeting: "Destinataires",
        is_onboarding: "Envoi automatique aux nouveaux employés",
        requires_acknowledgment: "Nécessite acceptation (case à cocher)",
        requires_signature: "Nécessite une signature",
      },
    },

    // Tabs
    tabs: {
      content: "Contenu",
      details: "Détails",
      attachments: "Pièces jointes",
      recipients: "Destinataires",
      analytics: "Analyses",
    },

    // Options (for select/enum form fields and faceted filters)
    options: {
      status: {
        draft: "Brouillon",
        sent: "Envoyé",
        archived: "Archivé",
      },
      priority: {
        low: "Faible",
        normal: "Normal",
        high: "Élevé",
        urgent: "Urgent",
      },
      channels: {
        in_app: "Dans l’application",
        push: "Notification push",
        email: "Courriel",
      },
    },

    // Badges (for select options and badge columns)
    badges: {
      draft: "Brouillon",
      scheduled: "Planifié",
      sent: "Envoyé",
      archived: "Archivé",
      low: "Faible",
      normal: "Normal",
      high: "Élevé",
      urgent: "Urgent",
      in_app: "Dans l’application",
      push: "Notification push",
      email: "Courriel",
    },

    // Actions
    actions: {
      send: "Envoyer",
      schedule: "Planifier",
      archive: "Archiver",
      bulk_send: "Envoyer la sélection",
      bulk_archive: "Archiver la sélection",
    },

    // Filters
    filters: {
      status: "Statut",
      priority: "Priorité",
      is_onboarding: "Intégration",
      is_scheduled: "Planifié",
    },

    // Schedule Manager
    schedule_manager: {
      title: "Gestionnaire de planification",
      send_now: "Envoyer maintenant",
      schedule_new: "Planifier un envoi",
      send_now_description: "Envoyer cette communication immédiatement aux destinataires sélectionnés.",
      schedule: "Planifier",
      date: "Date",
      time: "Heure",
      frequency: "Fréquence",
      scheduled_time_future: "L'heure planifiée doit être au moins 5 minutes dans le futur.",
      scheduled_time_min_hint: "Choisissez une heure au moins 5 minutes après maintenant.",
      select_recipient: "Veuillez sélectionner au moins un destinataire.",
      archived_unavailable: "La planification n'est pas disponible pour les communications archivées.",
      schedule_history: "Historique de planification",
      no_scheduled_items: "Aucun élément planifié trouvé.",
      scheduled_for: "Planifié pour : :date",
      executed: "Exécuté",
      executed_at: "Exécuté : :date",
      cancelled: "Annulé",
      cancelled_at: "Annulé : :date",
      pending: "En attente",
      cancel: "Annuler",
      retract: "Retirer",
      retract_blocked: "Retrait impossible : au moins un destinataire a déjà lu ce message.",
      retract_confirm: "Retirer cette annonce de toutes les boîtes de réception ? Cette action est irréversible.",
    },

    // Recipient Picker
    recipients: {
      title: "Destinataires",
      target_audience: "Public cible",
      specific_users: "Employés spécifiques",
      specific_users_description: "Sélectionner des utilisateurs individuels",
      by_roles: "Par rôle",
      by_roles_description: "Cibler les utilisateurs par rôle",
      all_users: "Tous les utilisateurs",
      all_users_description: "Envoyer à tout le monde",
      all_users_message: "Tous les utilisateurs recevront cette communication",
      all_users_description_full: "Cette communication sera envoyée à tout le monde dans le système.",
      select_users: "Sélectionner les utilisateurs",
      select_roles: "Sélectionner les rôles",
      select_all: "Tout sélectionner",
      deselect_all: "Tout désélectionner",
      exclude_users: "Exclure des utilisateurs spécifiques (optionnel)",
      search_users: "Rechercher des utilisateurs...",
      search_users_exclude: "Rechercher des utilisateurs à exclure...",
      type_to_search: "Tapez pour rechercher des utilisateurs...",
      searching: "Recherche...",
      type_min_chars: "Tapez au moins 2 caractères pour rechercher",
      no_users_found: "Aucun utilisateur trouvé",
      users: "Employés",
      selected: "Sélectionné",
      users_selected: ":count / :max utilisateurs sélectionnés",
      maximum_selected: "Maximum :max utilisateurs sélectionnés",
      more: "+:count de plus",
      preview: "Aperçu",
      configure_preview: "Configurez les destinataires ci-dessus pour voir un aperçu",
      calculating: "Calcul des destinataires...",
      preview_failed: "Échec de l'aperçu des destinataires",
      no_users_match: "Aucun utilisateur ne correspond à ces critères",
      try_different: "Essayez de sélectionner différents utilisateurs ou rôles",
      recipient: "destinataire",
      recipients_plural: "destinataires",
      large_audience: "Grand public",
      show_sample: "Afficher les destinataires",
      hide_sample: "Masquer les destinataires",
      cannot_modify: "Les destinataires ne peuvent pas être modifiés après l'envoi de la communication.",
      role: "rôle",
      roles: "rôles",
      user: "utilisateur",
      users_plural: "utilisateurs",
      total: "total",
      roles_selected: ":count :role sélectionné",
      no_roles_available: "Aucun rôle disponible",
      failed_to_load_roles: "Échec du chargement des rôles",
      try_again: "Réessayer",
    },

    // Responder Picker
    responders: {
      title: "Répondants de conversation",
      description: "Sélectionnez qui sera automatiquement ajouté aux conversations lorsque les destinataires répondent.",
      search_users: "Rechercher des utilisateurs à ajouter comme Répondants...",
      preview: "Aperçu des Répondants",
      configure_preview: "Sélectionnez des rôles ou des utilisateurs ci-dessus pour voir un aperçu",
      calculating: "Résolution des Répondants...",
      preview_failed: "Échec de l'aperçu des Répondants",
      no_responders_match: "Aucun répondeur ne correspond aux critères sélectionnés",
      responder: "répondeur",
      responders_plural: "Répondants",
      cannot_modify: "Les répondants ne peuvent pas être modifiés après l'envoi de la communication.",
      permissions: "Autorisations des Répondants",
      can_view: "Peut visualiser les messages de conversation",
      can_reply: "Peut répondre aux conversations",
      none_summary: "Aucun répondant",
    },

    // Analytics Panel
    analytics: {
      title: "Analyses",
      not_available: "Analyses non disponibles",
      not_available_description: "Les analyses seront disponibles après l'envoi de cette communication.",
      scheduled_delivery: "Planifié pour la livraison",
      scheduled_delivery_description: "Les analyses seront disponibles après l'heure de livraison planifiée.",
      failed_to_load: "Échec du chargement des analyses",
      retry: "Réessayer",
      sent: "Envoyé",
      read: "Lu",
      read_rate: "Taux de lecture",
      acknowledged: "Accepté",
      delivery_funnel: "Entonnoir de livraison",
      funnel_chip_total: ":count au total",
      funnel_chip_unique: ":count uniques",
      prior_delivery_label: "Envoi précédent n° :n",
      delivery_summary: "Résumé de la livraison",
      total_recipients: "Total des destinataires",
      delivery_rate: "Taux de livraison",
      open_rate: "Taux d'ouverture",
      acknowledgment_rate: "Taux de reconnaissance",
      avg_response_time: "Temps de réponse moyen",
      no_analytics_yet: "Pas encore d'analyses",
      send_to_see: "Envoyez cette communication pour voir les analyses de livraison.",
      recipients: "Destinataires",
      search_recipients: "Rechercher des destinataires...",
      filter_by_status: "Filtrer par statut",
      all: "Tous",
      pending: "En attente",
      sent_label: "Envoyé",
      read_label: "Lu",
      pending_ack: "Reconnaissance en attente",
      acknowledged_label: "Accepté",
      recipient_column: "Destinataire",
      status_column: "Statut",
      sent_column: "Envoyé",
      read_column: "Lu",
      acknowledged_column: "Accepté",
      no_recipients_match: "Aucun destinataire ne correspond à vos filtres",
      recipient_count: ":count :label",
      page: "Page :current sur :total",
      csv: "CSV",
      excel: "Excel",
      just_now: "À l'instant",
      minutes_ago: "il y a :countm",
      hours_ago: "il y a :counth",
      days_ago: "il y a :countj",
      pending_signature: "Signature en attente",
      signed: "Signé",
      view_signature: "Voir la signature",
      signature_column: "Signature",
      signature_viewer_title: "Signature",
      signed_by: "Signé par :name",
      signed_on: "Signé le :date",
      signature_rate: "Taux de signature",
    },

    // Communication Detail Page
    detail: {
      from: "De :",
      sent: "Envoyé :",
      read: "Lu :",
      acknowledged_on: "Accepté le :date",
      requires_acknowledgment: "Nécessite une reconnaissance",
      attachments: "Pièces jointes (:count)",
      download: "Télécharger",
      acknowledgment_required: "Confirmation de lecture",
      acknowledgment_description: "Merci de confirmer votre lecture.",
      acknowledgment_checkbox: "J'ai lu et compris cette communication",
      comment_optional: "Commentaire (optionnel)",
      add_comment: "Ajouter un commentaire...",
      acknowledge: "Reconnaître",
      acknowledging: "Reconnaissance...",
    },

    // Schedule Frequency
    schedule: {
      frequency: {
        once: "Une fois",
        daily: "Quotidien",
        weekly: "Hebdomadaire",
        monthly: "Mensuel",
      },
    },

    // Placeholders
    placeholders: {
      select_date_time: "Sélectionner la date et l'heure",
      media_placeholder: "Sélectionner les fichiers multimédias",
    },

    // Media Library Integration
    media: {
      requires_spatie: "(Nécessite l'intégration de la bibliothèque média Spatie)",
    },
  },
};
